// AdminFlags — list + set + delete feature flags (global or tenant-scoped).

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';

interface FeatureFlag {
  id: string;
  tenant_id: string | null;
  key: string;
  value: unknown;
  set_by_platform_user_id: string;
  created_at: Date;
  updated_at: Date;
}

export function AdminFlags(): ReactElement {
  const qc = useQueryClient();
  const [tenant_id, set_tenant_id] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['admin-flags', tenant_id],
    queryFn: () => orpc.admin.feature_flags.list({ tenant_id }),
  });

  const set_m = useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      orpc.admin.feature_flags.set({ tenant_id, key: input.key, value: input.value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-flags'] }),
  });
  const del_m = useMutation({
    mutationFn: (key: string) =>
      orpc.admin.feature_flags.delete({ tenant_id, key }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-flags'] }),
  });

  const flags = (q.data?.flags ?? []) as FeatureFlag[];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-medium">Feature flags</h1>
        <p className="text-xs text-ink/60 mt-1">
          Per-tenant overrides + global defaults. Tenant-specific wins over global.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-xs text-ink/70">Scope:</label>
        <input
          type="text"
          value={tenant_id ?? ''}
          onChange={(e) => set_tenant_id(e.target.value.trim() || null)}
          placeholder="(empty = global) or tenant-id"
          className="px-3 py-1 border border-line rounded text-sm font-mono min-w-[280px]"
        />
        <span className="text-xs text-ink/50">
          {tenant_id === null ? 'Global flags' : 'Tenant-specific'}
        </span>
      </div>

      <AddFlagForm
        on_submit={(key, value) => set_m.mutate({ key, value })}
        busy={set_m.isPending}
      />

      {q.isLoading && <div className="text-sm text-ink/60 mt-4">Loading…</div>}
      {flags.length > 0 && (
        <div className="bg-surface border border-line rounded-lg overflow-hidden mt-4">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev border-b border-line text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Key</th>
                <th className="text-left px-4 py-2 font-medium">Value</th>
                <th className="text-left px-4 py-2 font-medium">Updated</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.id} className="border-b border-line/40 last:border-b-0">
                  <td className="px-4 py-2 font-mono">{f.key}</td>
                  <td className="px-4 py-2 font-mono text-xs">{JSON.stringify(f.value)}</td>
                  <td className="px-4 py-2 text-xs text-ink/60">
                    {new Date(f.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => del_m.mutate(f.key)}
                      disabled={del_m.isPending}
                      className="text-xs px-2 py-1 border border-bad/30 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AddFlagForm({
  on_submit,
  busy,
}: {
  on_submit: (key: string, value: unknown) => void;
  busy: boolean;
}): ReactElement {
  const [key, set_key] = useState('');
  const [value, set_value] = useState('true');
  const [err, set_err] = useState<string | null>(null);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      set_err('Key must be snake_case (lowercase a-z + _)');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      set_err('Value must be valid JSON');
      return;
    }
    set_err(null);
    on_submit(key, parsed);
    set_key('');
    set_value('true');
  };

  return (
    <form onSubmit={submit} className="bg-surface border border-line rounded-lg p-4 flex gap-2 items-end">
      <label className="flex-1">
        <span className="block text-xs font-medium text-ink/70 mb-1">Key</span>
        <input
          type="text"
          value={key}
          onChange={(e) => set_key(e.target.value)}
          placeholder="autopay_enabled"
          className="w-full px-3 py-1.5 border border-line rounded text-sm font-mono"
          required
        />
      </label>
      <label className="flex-1">
        <span className="block text-xs font-medium text-ink/70 mb-1">Value (JSON)</span>
        <input
          type="text"
          value={value}
          onChange={(e) => set_value(e.target.value)}
          placeholder="true"
          className="w-full px-3 py-1.5 border border-line rounded text-sm font-mono"
          required
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="bg-primary hover:bg-primary-2 text-white px-4 py-1.5 rounded text-sm font-medium disabled:opacity-50"
      >
        {busy ? '…' : 'Set'}
      </button>
      {err && <span className="text-xs text-bad ml-2">{err}</span>}
    </form>
  );
}
